import React, {useMemo} from 'react';
import {Stack} from '@mui/material';
import type {Schema} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import {getGroupedParts} from 'schemaDefinition/functions';
import {FieldLayout} from 'components/fields/FieldLayout';
import {GroupLabel} from 'components/fields/GroupLabel';
import {Layout} from 'components/layout';
import {
  FormSchemaGlobalScopeContextProvider,
  FormSchemaLocalScopeContextProvider,
} from 'schema/contexts';
import type {BasePartProps} from './types';
import {useSchemaContainer} from '../hooks/useSchemaContainer';
// eslint-disable-next-line import/no-cycle
import {DataFormSchemaFragment} from './DataFormSchemaFragment';

type DataFormSchemaProps = Omit<
  BasePartProps,
  'scopePath' | 'usesGlobalScope' | 'value'
> & {
  schema: Schema;
};

/**
 * Sets local scope and renders form for given schema x value.
 * Use to render both global schema and part schemas on lower level.
 * @returns
 */
export const DataFormSchema: React.FC<DataFormSchemaProps> = ({
  schema,
  valuePath,
  relatedScope,
  mode,
  diff,
  noLabel = false,
  useValue,
  useOriginalValue,
}) => {
  const {tLoose} = useLocalization();

  const groupedParts = useMemo(
    () =>
      getGroupedParts(schema.parts, 'form').map(group => ({
        title: group.titleKey
          ? tLoose(`form.group.${group.titleKey}.title`)
          : undefined,
        parts: group.parts,
      })),
    [schema.parts, tLoose],
  );

  const Container = useSchemaContainer(schema);

  return (
    <FormSchemaGlobalScopeContextProvider
      schema={schema.globalScopePath ? schema : undefined}
      valuePath={schema.globalScopePath}>
      <FormSchemaLocalScopeContextProvider
        schema={schema}
        valuePath={valuePath}>
        <Stack spacing={2}>
          {groupedParts.map((group, idx) =>
            // TODO: Schema layout config
            schema.name === 'changeRequest' ? (
              <FieldLayout key={idx}>
                <DataFormSchemaFragment
                  useValue={useValue}
                  useOriginalValue={useOriginalValue}
                  valuePath={valuePath}
                  relatedScope={relatedScope}
                  fragment={group.parts}
                  mode={mode}
                  diff={diff}
                  noLabel={noLabel}
                />
              </FieldLayout>
            ) : (
              <Layout key={idx}>
                {group.title && <GroupLabel sx={{pl: 2}} label={group.title} />}
                <Container>
                  <FieldLayout>
                    <DataFormSchemaFragment
                      useValue={useValue}
                      useOriginalValue={useOriginalValue}
                      valuePath={valuePath}
                      relatedScope={relatedScope}
                      fragment={group.parts}
                      mode={mode}
                      diff={diff}
                      noLabel={noLabel}
                    />
                  </FieldLayout>
                </Container>
              </Layout>
            ),
          )}
        </Stack>
      </FormSchemaLocalScopeContextProvider>
    </FormSchemaGlobalScopeContextProvider>
  );
};
